/**
 * The navigation drawer's submenu panels.
 *
 * SCOPE: everything here is looked up inside [data-calmy-nav] and nothing is
 * bound outside it. Opening and closing the drawer itself, the overlay, the
 * scroll lock and the focus trap all stay in calmy-core.js - this file only
 * swaps which view inside the open drawer is showing.
 *
 * WHY hidden RATHER THAN A CLASS: core's focus trap collects focusable
 * elements by offsetParent, so a view hidden with the attribute drops out of
 * the tab order for free. No aria-hidden juggling and no inert to keep in sync.
 *
 * Escape goes back one level first and closes the drawer only from the root,
 * which is what a reader expects from a panel that has a back button. Core's
 * own Escape handler is not touched; this one runs in the capture phase and
 * stops the event only when it has something to go back from.
 */
(function () {
  "use strict";

  var PANEL = "[data-calmy-nav]";

  function panel() { return document.querySelector(PANEL); }
  function body(p) { return p.querySelector("[data-calmy-nav-body]"); }
  function views(p) { return [].slice.call(p.querySelectorAll("[data-calmy-nav-sub]")); }
  function openView(p) {
    return views(p).filter(function (v) { return !v.hidden; })[0] || null;
  }
  function reduced() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function show(p, id, trigger) {
    var b = body(p);
    var target = p.querySelector('[data-calmy-nav-sub="' + id + '"]');
    if (!b || !target) return;
    views(p).forEach(function (v) {
      v.hidden = v !== target;
      if (v !== target) v.classList.remove("is-in");
    });
    b.hidden = true;
    b.classList.remove("is-back");
    if (!reduced()) { target.classList.remove("is-in"); void target.offsetWidth; target.classList.add("is-in"); }
    target.__calmyFrom = trigger || null;
    /* The back button, not the first link: it is where the reader arrived and
       the one control that undoes the step they just took. */
    var back = target.querySelector("[data-calmy-nav-back]");
    if (back) { try { back.focus({ preventScroll: true }); } catch (e) {} }
    p.querySelectorAll("[data-calmy-nav-open]").forEach(function (t) {
      t.setAttribute("aria-expanded", t.getAttribute("data-calmy-nav-open") === id ? "true" : "false");
    });
  }

  function back(p) {
    var open = openView(p);
    var b = body(p);
    if (!open || !b) return false;
    open.hidden = true;
    open.classList.remove("is-in");
    b.hidden = false;
    if (!reduced()) { b.classList.remove("is-back"); void b.offsetWidth; b.classList.add("is-back"); }
    p.querySelectorAll("[data-calmy-nav-open]").forEach(function (t) {
      t.setAttribute("aria-expanded", "false");
    });
    var from = open.__calmyFrom;
    open.__calmyFrom = null;
    if (from && document.contains(from)) { try { from.focus({ preventScroll: true }); } catch (e) {} }
    return true;
  }

  /* Reopening the drawer starts at the main menu: a reader who closed it
     inside Rings is starting something new when they open it again. */
  function reset(p) {
    var b = body(p);
    views(p).forEach(function (v) { v.hidden = true; v.classList.remove("is-in"); v.__calmyFrom = null; });
    if (b) { b.hidden = false; b.classList.remove("is-back"); }
    p.querySelectorAll("[data-calmy-nav-open]").forEach(function (t) {
      t.setAttribute("aria-expanded", "false");
    });
  }

  /* One delegated listener on the document, so markup replaced by the theme
     editor is wired without rebinding anything. */
  document.addEventListener("click", function (e) {
    var p = panel();
    if (!p) return;
    var opener = e.target.closest && e.target.closest("[data-calmy-nav-open]");
    if (opener && p.contains(opener)) {
      e.preventDefault();
      show(p, opener.getAttribute("data-calmy-nav-open"), opener);
      return;
    }
    var backBtn = e.target.closest && e.target.closest("[data-calmy-nav-back]");
    if (backBtn && p.contains(backBtn)) {
      e.preventDefault();
      back(p);
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    var p = panel();
    if (!p || !p.classList.contains("is-on")) return;
    if (back(p)) { e.stopPropagation(); }
  }, true);

  /* The drawer is closed from several places - the X, the overlay, Escape at
     the root, a link that navigates - and they all end in the same class being
     removed. Watching for that is one hook instead of four. */
  function watch() {
    var p = panel();
    if (!p || p.__calmyNavSub) return;
    p.__calmyNavSub = true;
    if (!("MutationObserver" in window)) return;
    new MutationObserver(function () {
      if (!p.classList.contains("is-on")) reset(p);
    }).observe(p, { attributes: true, attributeFilter: ["class"] });
  }

  /* The localization form: submitting on change saves a tap, and the button
     stays in the markup for anyone without this script. */
  document.addEventListener("change", function (e) {
    var sel = e.target;
    if (!sel || sel.tagName !== "SELECT") return;
    var form = sel.closest && sel.closest("[data-calmy-nav-loc]");
    if (!form) return;
    if (typeof form.requestSubmit === "function") form.requestSubmit();
    else form.submit();
  });

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", watch);
  else watch();
  /* calmy-core.js moves the drawer to <body> on load and the editor re-renders
     it on any setting change; both end with a different element in the DOM. */
  document.addEventListener("shopify:section:load", function () { watch(); });
  window.setTimeout(watch, 300);
})();
